import { useState, useEffect, useContext, useMemo, useRef } from "react";
import { Modal, ModalBody } from "reactstrap";
import BaseChart from "../../../../../components/BaseChart";
import styles from "../../Dashboard.module.css";
import helper from "../../../../../utils/helper";
import { DashboardOutorgaContext } from "../../../../../contexts/DashboardOutorga";
import ReactLoading from "react-loading";
import { getStatusColor } from "../../../../../utils/outorga/dashboard";
import { formatTimestampLabel } from "../../../../../utils/normalizeDateHelpers";
import FullChartsFilterRow from "./FullChartsFilterRow";
import { useFullChartsExport } from "../../../../../hooks/outorga/useFullChartsExport";

const FullCharts = ({ showFullCharts, grantId, closeFullCharts }) => {
    const { fullCharts, isLoadingFullCharts, getGrantDashboardFullCharts, clearFullCharts } = useContext(DashboardOutorgaContext);
    const groupByOptions = [
        { value: "hora", label: "Hora" },
        { value: "dia", label: "Dia" },
        { value: "mes", label: "Mês" }
    ];

    const [period, setPeriod] = useState({});
    const [groupBy, setGroupBy] = useState(groupByOptions[0]);
    const [tipoVisualizacao, setTipoVisualizacao] = useState("todos");
    const [mostrarExcedidos, setMostrarExcedidos] = useState(true);
    const [mostrarInicioDia, setMostrarInicioDia] = useState(true);
    const summaryScrollRef = useRef(null);
    const [canScrollLeft, setCanScrollLeft] = useState(false);
    const [canScrollRight, setCanScrollRight] = useState(false);
    const modalContentRef = useRef(null);
    const filterRowRef = useRef(null);
    const chartAreaRef = useRef(null);

    useEffect(() => {
        if (showFullCharts && grantId && Object.keys(period).length && !data) {
            getChartsData();
        }
    }, [showFullCharts, grantId, period]);

    useEffect(() => {
        if (data && Object.keys(period).length && groupBy?.value) {
            getChartsData();
        }
    }, [groupBy]);

    const getChartsData = () => {
        const { startDate, endDate } = period;

        const payload = {
            periodo: {
                data_inicio: startDate,
                data_fim: endDate
            },
            agrupar_por: groupBy.value
        };

        getGrantDashboardFullCharts(grantId, payload);
    };

    const data = useMemo(() => {
        if (!fullCharts) return null;

        return {
            nome: fullCharts.nome,
            resumoGeral: fullCharts?.resumoGeral?.flatMap(item => {
                return [
                    {
                        id: `${item.id}-${item.grafico_id}-atual`,
                        label: `${item.nome} - Atual`,
                        value: item.valor_reporte,
                        status: item.status,
                        chartId: item.grafico_id
                    },
                    {
                        id: `${item.id}-${item.grafico_id}-media`,
                        label: `${item.nome} - Média`,
                        value: item.valor_media,
                        chartId: item.grafico_id
                    },
                    {
                        id: `${item.id}-${item.grafico_id}-max`,
                        label: `${item.nome} - Máxima`,
                        value: item.valor_maxima,
                        chartId: item.grafico_id
                    },
                    {
                        id: `${item.id}-${item.grafico_id}-min`,
                        label: `${item.nome} - Mínima`,
                        value: item.valor_minima,
                        chartId: item.grafico_id
                    },
                    {
                        id: `${item.id}-${item.grafico_id}-excedido`,
                        label: `${item.nome} - Excedido`,
                        value: `${item.quantidade_excedeu_maximo}x`,
                        isExcess: true,
                        excess: item.quantidade_excedeu_maximo >= 1,
                        chartId: item.grafico_id
                    }
                ]
            }),
            charts: fullCharts?.graficos?.map(grafico => {
                const condicao = grafico.parametro_principal?.condicao || {};

                const linhas = [
                    condicao.alerta && {
                        label: "Alerta (Início)",
                        valor: condicao?.alerta?.inicio,
                        cor: "#f8bb54"
                    },
                    condicao.alerta && {
                        label: "Alerta (Fim)",
                        valor: condicao?.alerta?.fim,
                        cor: "#f8bb54"
                    },
                    condicao.critico && {
                        label: "Crítico (Início)",
                        valor: condicao?.critico?.inicio,
                        cor: "#dc8e09"
                    },
                    condicao.critico && {
                        label: "Crítico (Fim)",
                        valor: condicao?.critico?.fim,
                        cor: "#dc8e09"
                    }
                ].filter(Boolean);

                return {
                    id: grafico.id,
                    descricao: grafico.parametro_principal.nome,
                    xAxis: grafico.eixo_time,
                    series: [
                        {
                            name: grafico.parametro_principal.nome,
                            data: grafico.valores,
                            tipo: grafico.parametro_principal.tipo_grafico === "linha" ? "line" : "bar",
                        },
                        ...grafico.parametros_mesclados.map((parametro) => ({
                            name: parametro.nome,
                            data: parametro.valores ?? [],
                            tipo: parametro.tipo_grafico === "linha" ? "line" : "bar"
                        }))
                    ],
                    referencias: {
                        excedido: {
                            inicio: condicao?.excedido?.inicio,
                            fim: condicao.excedido?.fim
                        },
                        linhas
                    }
                };
            }),
            horasPeriodo: fullCharts?.horasPeriodo
        };
    }, [fullCharts]);

    const chartsFiltrados = useMemo(() => {
        if (!data?.charts) return [];
        if (tipoVisualizacao === "todos") return data.charts;
        return data.charts.filter(chart => chart.id === tipoVisualizacao);
    }, [data, tipoVisualizacao]);

    const resumosFiltrados = useMemo(() => {
        if (!data?.resumoGeral) return [];
        if (tipoVisualizacao === "todos") return data.resumoGeral;
        return data.resumoGeral.filter(resumo => resumo.chartId === tipoVisualizacao);
    }, [data, tipoVisualizacao]);

    const currentFilterLabel = useMemo(() => {
        if (tipoVisualizacao === "todos") return "Todos";

        const chart = data?.charts?.find(c => c.id === tipoVisualizacao);
        return chart?.descricao || "";
    }, [tipoVisualizacao, data]);

    const slideMove = (toRight) => {
        const container = summaryScrollRef.current;
        if (!container) return;

        container.scrollBy({
            left: toRight ? 300 : -300,
            behavior: "smooth"
        });
    };

    const checkScroll = () => {
        const container = summaryScrollRef.current;
        if (!container) return;

        setCanScrollLeft(container.scrollLeft > 0);
        setCanScrollRight(container.scrollLeft + container.clientWidth < container.scrollWidth);
    };

    useEffect(() => {
        const container = summaryScrollRef.current;
        if (!container) return;

        checkScroll();
        container.addEventListener("scroll", checkScroll);
        return () => container.removeEventListener("scroll", checkScroll);
    }, [data]);

    useEffect(() => {
        const container = summaryScrollRef.current;
        if (!container) return;

        container.scrollLeft = 0;
        checkScroll();
    }, [resumosFiltrados]);

    const handleSelectDate = (ranges) => {
        setPeriod({
            startDate: ranges?.startDate,
            endDate: ranges?.endDate
        });
    };

    const handleCloseFullCharts = () => {
        setPeriod({});
        clearFullCharts();
        setTipoVisualizacao("todos");
        setMostrarExcedidos(true);
        setMostrarInicioDia(true);
        setGroupBy(groupByOptions[0]);
        closeFullCharts();
    };

    const generateReferenceLines = (name, value, xAxis, color, dashed = false) => ({
        name,
        type: "line",
        data: xAxis.map(() => value),
        symbol: "none",
        smooth: false,
        lineStyle: {
            color,
            width: 2,
            type: dashed ? "dashed" : "solid"
        },
        itemStyle: {
            color,
        },
        tooltip: { show: true }
    });

    const generateVerticalLines = (hour, xAxis, label) => {
        const [targetHour, targetMinute] = hour.split(":").map(Number);
        const lines = [];

        xAxis.forEach((x, i) => {
            const hourLabel = x.split(" ")[1];
            const [currentAxisTime] = hourLabel.split(":").map(Number);

            if (currentAxisTime === targetHour) {
                const hourFraction = targetMinute / 60;

                lines.push({
                    name: `${label} - ${hour}h`,
                    xAxis: i + hourFraction
                });
            }
        });

        return lines;
    };

    const buildPreset = (chart) => {
        const baseSeries = chart.series.map(serie => ({
            ...serie,
            name: serie.name,
            type: serie.tipo
        }));

        const limitSeries = [];

        if (chart.referencias?.linhas) {
            chart.referencias.linhas.forEach(linha => {
                if (typeof linha.valor === "number") {
                    limitSeries.push(
                        generateReferenceLines(
                            linha.label,
                            linha.valor,
                            chart.xAxis,
                            linha.cor || helper.generateRandomColor(),
                            true
                        )
                    );
                }
            });
        }

        if (mostrarExcedidos && chart.referencias?.excedido?.inicio) {
            limitSeries.push(
                generateReferenceLines(
                    "Excedido (Início)",
                    chart.referencias.excedido.inicio,
                    chart.xAxis,
                    "#FF0000"
                )
            );
        }
        if (mostrarExcedidos && chart.referencias?.excedido?.fim) {
            limitSeries.push(
                generateReferenceLines(
                    "Excedido (Fim)",
                    chart.referencias.excedido.fim,
                    chart.xAxis,
                    "#FF0000"
                )
            );
        }

        const showVerticalLines =
            groupBy?.value === "hora" &&
            chart?.xAxis?.length > 1 &&
            mostrarInicioDia &&
            data.horasPeriodo &&
            chart.xAxis?.[0]?.includes?.(":")
            ;

        const verticalLines = showVerticalLines ?
            generateVerticalLines(data?.horasPeriodo?.inicio, chart.xAxis, "Início do dia") :
            [];

        return {
            grid: { top: 50, left: 50, right: 45, bottom: 48 },
            tooltip: { trigger: "axis" },
            legend: { show: true, right: 10, type: !isExporting ? 'scroll' : 'plain' },
            xAxis: { type: "category", data: chart.xAxis },
            yAxis: { type: "value" },
            series: [...baseSeries, ...limitSeries].map((serie, index) => ({
                ...serie,
                markLine: index === 0 && verticalLines.length
                    ? {
                        symbol: "none",
                        lineStyle: {
                            color: "#a0a0a08e",
                            width: 2,
                            type: "dashed"
                        },
                        label: {
                            formatter: "{b}",
                            position: "end",
                            fontSize: 10,
                            color: "#808080",
                        },
                        data: verticalLines
                    }
                    : undefined
            }))
        };
    };

    const { handleExportPDF, isExporting } = useFullChartsExport({
        modalContentRef,
        filterRowRef,
        styles,
        slideButtonSelector: `.${styles.fullChartsSummarySlideButton}`
    });

    return (
        <Modal
            isOpen={showFullCharts}
            toggle={handleCloseFullCharts}
            size="xl"
            className={styles.fullChartsModal}
        >
            <ModalBody className={styles.fullChartsModalBody}>
                {isExporting && (
                    <div
                        className="loading-screen-overlay"
                        style={{
                            background: "rgb(255,255,255)",
                            fontSize: "1.2rem",
                            fontWeight: "600"
                        }}
                    >
                        Exportando...
                    </div>
                )}
                <div ref={modalContentRef} className={styles.fullChartsModalContent}>
                    <div className={styles.fullChartsFixedHeader}>
                        <div className={styles.fullChartsTitleArea}>
                            <h4>Gráficos Detalhados</h4>
                            <span style={{ fontSize: "0.875rem" }}>
                                {data?.nome}
                            </span>
                        </div>

                        <div className={styles.fullChartsFiltersRow}>
                            <FullChartsFilterRow
                                ref={filterRowRef}
                                isExporting={isExporting}
                                period={period}
                                handleSelectDate={handleSelectDate}
                                groupBy={groupBy}
                                setGroupBy={setGroupBy}
                                tipoVisualizacao={tipoVisualizacao}
                                setTipoVisualizacao={setTipoVisualizacao}
                                mostrarExcedidos={mostrarExcedidos}
                                setMostrarExcedidos={setMostrarExcedidos}
                                mostrarInicioDia={mostrarInicioDia}
                                setMostrarInicioDia={setMostrarInicioDia}
                                data={data}
                                getChartsData={getChartsData}
                                handleExportPDF={handleExportPDF}
                                isLoadingFullCharts={isLoadingFullCharts}
                                groupByOptions={groupByOptions}
                                currentFilterLabel={currentFilterLabel}
                            />
                        </div>
                    </div>

                    {isLoadingFullCharts ? (
                        <div className={styles.loadingCalendar}>
                            <ReactLoading
                                type="spin"
                                color="var(--background-menu)"
                                width={50}
                            />
                        </div>
                    ) : (
                        <div ref={chartAreaRef} className={styles.fullChartsChartsScrollArea}>

                            <div className={styles.fullChartsGeneralInfos}>
                                {period.startDate && period.endDate && (
                                    <p>
                                        {formatTimestampLabel(period.startDate, "dd 'de' MMMM 'de' yyyy")}
                                        {" — "}
                                        {formatTimestampLabel(period.endDate, "dd 'de' MMMM 'de' yyyy")}
                                    </p>
                                )}
                                <p>{resumosFiltrados?.length} Pontos de dados</p>
                            </div>

                            <div className={styles.fullChartsGeneralSummary}>
                                {canScrollLeft && (
                                    <div
                                        className={`${styles.fullChartsSummarySlideButton} ${styles.fullChartsSummarySlideButtonLeft}`}
                                        onClick={() => slideMove(false)}
                                    >
                                        <i className="fa fa-angle-left fa-2x" />
                                    </div>
                                )}

                                <div ref={summaryScrollRef} className={styles.fullChartsSummaryScroll}>
                                    {(resumosFiltrados || []).map(item => (
                                        <div key={item.id} className={styles.fullChartsMetricCard}>
                                            <span>{item.label}</span>
                                            <strong
                                                className={`
                                                    ${styles.fullChartsMetricCardValue}
                                                    ${item.isExcess ? styles.fullChartsMetricCardIsExcess : ""}
                                                    ${item.excess ? styles.fullChartsMetricCardExcess : ""}
                                                `}
                                                style={{ color: item.status ? getStatusColor(item.status) : "" }}
                                            >
                                                {item.value}
                                            </strong>
                                        </div>
                                    ))}
                                </div>

                                {canScrollRight && (
                                    <div
                                        className={`${styles.fullChartsSummarySlideButton} ${styles.fullChartsSummarySlideButtonRight}`}
                                        onClick={() => slideMove(true)}
                                    >
                                        <i className="fa fa-angle-right fa-2x" />
                                    </div>
                                )}
                            </div>

                            {chartsFiltrados.map(chart => (
                                <div key={chart.id} className={styles.fullChartsChartBlock}>
                                    <div className={styles.fullChartsChartContainer}>
                                        <BaseChart
                                            key={`${chart.id}-${mostrarExcedidos}`}
                                            id={chart.id}
                                            tipo={chart.tipo}
                                            descricao={chart.descricao}
                                            xAxis={chart.xAxis}
                                            customOptions={buildPreset(chart)}
                                            chartStyle={{ height: "320px" }}
                                        />
                                    </div>
                                </div>
                            ))}

                            {!fullCharts && (
                                <div className={styles.loadingCalendar}>
                                    Nenhum gráfico disponível
                                </div>
                            )}
                        </div>
                    )}
                </div>
            </ModalBody>
        </Modal>
    );
};

export default FullCharts;